import { hasLiveSession } from '@/lib/sessionGuard';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { router } from 'router';

import {
  sessionState,
  useAuth,
  useChatSession,
  useConfig
} from '@chainlit/react-client';

import ChatSettingsModal from './components/ChatSettings';
import { ThemeProvider } from './components/ThemeProvider';
import AccessEnded from './components/access/AccessEnded';
import { useAccess } from './components/access/useAccess';
import { Loader } from '@/components/Loader';
import { Toaster } from '@/components/ui/sonner';

import { userEnvState } from 'state/user';

declare global {
  interface Window {
    cl_shadowRootElement?: HTMLDivElement;
    transports?: string[];
    theme?: {
      light: Record<string, string>;
      dark: Record<string, string>;
    };
  }
}

/** Routes that never hold a chat, so never open the chat socket. */
const SOCKETLESS_ROUTES = [
  '/dashboard',
  '/anomalies',
  '/external-signals',
  '/financial-settings',
  '/admin',
  '/notifications',
  '/activate',
  '/help',
  '/memory',
  '/profile',
  '/know-your-data',
  '/cash',
  '/sales',
  '/margin',
  '/today',
  '/business-profile',
  '/data'
];
const isSocketlessRoute = (pathname: string) =>
  SOCKETLESS_ROUTES.some((r) => pathname.startsWith(r));

function App() {
  const { config } = useConfig();

  const { isAuthenticated, data, isReady } = useAuth();
  const userEnv = useRecoilValue(userEnvState);
  const { connect, chatProfile, setChatProfile } = useChatSession();
  const session = useRecoilValue(sessionState);
  // Asked before the first socket opens: a socket reserves a container, and an
  // account whose access has ended must not take one only to be refused.
  const { access, checked: accessChecked } = useAccess(isAuthenticated);
  const accessEnded = access?.status === 'ended';

  // This component renders the RouterProvider, so it sits OUTSIDE router
  // context and cannot use useLocation(). It still has to know when the route
  // changes: a tab that opened on /anomalies and then navigated to the chat in
  // the same tab (the "Ask Signals why" handoff) never re-ran the effect below,
  // so it never got a socket — the chat looked dead while the question was
  // answered into a session the browser was not listening to.
  const [pathname, setPathname] = useState(window.location.pathname);
  useEffect(
    () => router.subscribe((state) => setPathname(state.location.pathname)),
    []
  );

  const configLoaded = !!config;

  const chatProfileOk = configLoaded
    ? config.chatProfiles.length
      ? !!chatProfile
      : true
    : false;

  useEffect(() => {
    if (!isAuthenticated || !isReady || !chatProfileOk) {
      return;
    }
    if (!accessChecked || accessEnded) {
      return;
    }
    // Pages without a chat are pure HTTP — no WebSocket session needed.
    // Connecting here would fire on_chat_start and reserve a Docker container
    // for a page that never uses it. Market, Financial settings and
    // Configurations were missing from this list until 2026-10-07.
    if (isSocketlessRoute(pathname)) {
      return;
    }
    // A socket IS a container: connect() closes the old one and opens a new one,
    // so this must never run for a tab whose session is alive -- or still
    // coming alive, since `connected` is false for the whole handshake and a
    // second connect() there would leave two containers behind.
    //
    // `active` is exactly that predicate: socket.io keeps it true while the
    // socket is connected or retrying, and sets it false only after an
    // explicit disconnect(). clear() (New Chat) calls disconnect() but leaves
    // the socket object in Recoil, so the old `if (session?.socket)` guard saw
    // a truthy socket and returned -- the tab never reconnected, the composer
    // stayed disabled behind `!connected`, and New Chat did nothing until the
    // user pressed F5.
    if (hasLiveSession(session)) {
      return;
    }

    connect({
      transports: window.transports,
      userEnv
    });
  }, [
    userEnv,
    isAuthenticated,
    connect,
    isReady,
    chatProfileOk,
    pathname,
    session,
    accessChecked,
    accessEnded
  ]);

  useEffect(() => {
    if (
      !configLoaded ||
      !config ||
      !config.chatProfiles?.length ||
      chatProfile
    ) {
      return;
    }

    const defaultChatProfile = config.chatProfiles.find(
      (profile) => profile.default
    );

    if (defaultChatProfile) {
      setChatProfile(defaultChatProfile.name);
    } else {
      setChatProfile(config.chatProfiles[0].name);
    }
  }, [configLoaded, config, chatProfile, setChatProfile]);

  if (!configLoaded && isAuthenticated) return null;

  return (
    <ThemeProvider
      storageKey="vite-ui-theme"
      defaultTheme={data?.default_theme}
    >
      <Toaster richColors className="toast" position="top-right" />

      {isAuthenticated && accessEnded && access ? (
        <AccessEnded access={access} />
      ) : (
        <>
          <ChatSettingsModal />
          <RouterProvider router={router} />
        </>
      )}

      <div
        className={cn(
          'bg-[hsl(var(--background))] flex items-center justify-center fixed size-full p-2 top-0',
          (isReady || isSocketlessRoute(window.location.pathname)) && 'hidden'
        )}
      >
        <Loader className="!size-6" />
      </div>
    </ThemeProvider>
  );
}

export default App;
